import type { ICellValue, IDataRow, ILinkValue } from "@/api/molecularData";

export interface IColumnEntry {
  key: string;
  displayName: string;
}

export const MOLECULE_COL_KEY = "__molecule__";
export const MOLECULE_WIDTH = 150;
export const MOLECULE_HEIGHT = 110;
export const BSAD_ID_COL_KEY = "BSA-DB ID";

export const NO_INFO_COLS = new Set([
  "No.",
  "Molecule",
  "SMILES",
  "Viruses",
  "Number of Viruses",
]);

// ── Column width constants ────────────────────────────────────────────────────
// At paragraph-m (12px), average proportional char ≈ 7 px. px-4 padding = 32 px.
const MAX_DISPLAY_CHARS = 25;
const TRUNCATE_CHARS = 17;
const CHAR_WIDTH_PX = 7;
const COL_PADDING_PX = 32;
export const MIN_COL_WIDTH_PX = 60;
// Small icon-only Button (~28 px) minus the -ml-1.5 (6 px) offset = ~22 px extra.
const INFO_BUTTON_PX = 22;

// ── Array-cell line-height constants ─────────────────────────────────────────
// paragraph-m: 12 px × 1.5 line-height = 18 px; gap-1 = 4 px between items.
// Max lines that fit in (MOLECULE_HEIGHT − one_line): floor((110 − 18 + 4) / (18 + 4)) = floor(96/22) = 4.
const ARRAY_LINE_PX = 18;
const ARRAY_GAP_PX = 4;
export const MAX_ARRAY_LINES = Math.floor(
  (MOLECULE_HEIGHT - ARRAY_LINE_PX + ARRAY_GAP_PX) /
    (ARRAY_LINE_PX + ARRAY_GAP_PX),
); // = 4

// ── Text helpers ──────────────────────────────────────────────────────────────

export function truncate(text: string): string {
  return text.length > MAX_DISPLAY_CHARS
    ? text.slice(0, TRUNCATE_CHARS) + "..."
    : text;
}

function valueDisplayLen(value: ICellValue): number {
  if (value === null || value === undefined) return 1;
  if (Array.isArray(value)) {
    const arr = value as (string | number)[];
    if (arr.length === 0) return 0;
    return Math.min(
      Math.max(...arr.map((item) => String(item ?? "—").length)),
      MAX_DISPLAY_CHARS,
    );
  }
  if (typeof value === "object" && "id" in value) {
    // Coerce id to string — the real API may return numeric IDs at runtime
    return Math.min(
      String((value as ILinkValue).id ?? "").length,
      MAX_DISPLAY_CHARS,
    );
  }
  return Math.min(String(value).length, MAX_DISPLAY_CHARS);
}

export function colWidthPx(
  col: IColumnEntry,
  rows: IDataRow[],
  hasInfoButton: boolean,
): number {
  const headerLen = Math.min(col.displayName.length, MAX_DISPLAY_CHARS);
  const maxValLen = rows.reduce((max, row) => {
    const len = valueDisplayLen(row[col.key] ?? null);
    // Guard against NaN propagation (Math.max(x, NaN) === NaN)
    return Number.isFinite(len) ? Math.max(max, len) : max;
  }, 0);
  const headerPx =
    headerLen * CHAR_WIDTH_PX +
    COL_PADDING_PX +
    (hasInfoButton ? INFO_BUTTON_PX : 0);
  const valPx = maxValLen * CHAR_WIDTH_PX + COL_PADDING_PX;
  const px = Math.max(MIN_COL_WIDTH_PX, headerPx, valPx);
  return Number.isFinite(px) ? px : MIN_COL_WIDTH_PX;
}

// ── Type guards ───────────────────────────────────────────────────────────────

export function isLinkValue(value: ICellValue): value is ILinkValue {
  return (
    typeof value === "object" &&
    value !== null &&
    "id" in value &&
    "link" in value
  );
}

export function isArrayValue(value: ICellValue): value is (string | number)[] {
  return Array.isArray(value);
}
